import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  runDroppedRate,
  runFulfillmentRate,
  runMatchRate,
} from "../../utils/matchRate";
import { ScheduledJobRun } from "./types";

// bd-deepcontext-hfv7u.4 — composition-bars match-rate chart.
//
// Renders one bar per run in chronological order: the green portion is the
// run's match rate (matched / total, guarded against total = 0 in
// `runMatchRate`), the red portion is the remainder. A polyline traces the
// match-rate boundary across runs (the trend), a tick under each bar encodes
// run status, and failed / running / pending bars are dimmed. The x-axis groups
// runs by day with dividers + a day label, and prints each run's time. Hovering
// a bar shows a tooltip (timestamp / status / rate / matched / unmatched /
// processed / duration); clicking selects the run via the modal-owned
// `onSelectRun`. Selection state stays in JobRunsModal — this component only
// reads `selectedRunId` and fires `onSelectRun`, keeping the prop boundary that
// bd-hfv7u.3 established intact.
export interface JobRunsChartProps {
  runs: ScheduledJobRun[];
  selectedRunId: string | null;
  onSelectRun: (runId: string) => void;
  // bd-hfv7u.10 — the windowed modal renders the title / range / pager / legend
  // in its own header row (so the pager sits beside the range label, per the
  // agreed design). Pass false there to suppress this component's inline header.
  showHeader?: boolean;
  // bd-hfv7u.13 — the job's per-run URL target ("requested"). When present (> 0)
  // the chart draws a second amber dashed FULFILLMENT line (urlsProcessed /
  // urlsPerRun) on the same 0–100% axis as the blue match-rate line, and adds the
  // requested/fulfilled rows to the tooltip + an amber legend entry. Omitted or 0
  // → no fulfillment line (divide-by-zero guard). Only the CURRENT target is
  // stored, so it's the denominator for every run in the window.
  urlsPerRun?: number;
}

type RunStatus = ScheduledJobRun["status"];

// Plot geometry (viewBox units). The SVG scales responsively to its container.
// bd-hfv7u.12 — the chart uses a FIXED viewBox width and stretches to fill the
// modal body (default preserveAspectRatio so it spans full width, not centred),
// with a left gutter reserved for the √-scaled Y axis (bd-hfv7u.16) per mockup.
const PLOT_WIDTH = 860; // fixed plot width (viewBox units); SVG scales to 100%
const PLOT_HEIGHT = 120; // height of the bars area
const TICK_AREA = 14; // status-tick row under the bars
const AXIS_HEIGHT = 34; // time + day-label row
const TOP_PAD = 8; // headroom so a full-height bar doesn't touch the edge
const Y_AXIS_W = 34; // left gutter for the Y axis labels (0/50/100)
const RIGHT_PAD = 12; // right breathing room so the last bar isn't clipped

// bd-deepcontext-od1h — every colour below is a CSS custom property (index.css:
// :root for light, .dark for dark), never a literal hex, so the chart repaints
// under either theme. Legend swatches (rendered here and duplicated in
// JobRunsModal's header) reuse these same tokens as the marks they describe.
export const CHART_COLORS = {
  matched: "var(--safe-fg)",
  // A soft tone derived from the risk family, distinct from --risk-fg
  // ("failed") so the two read as different signals on the same bar.
  unmatched: "var(--chart-risk-soft)",
  droppedOrFulfillment: "var(--warn-fg)",
  failed: "var(--risk-fg)",
  trend: "var(--ink)",
  gridline: "var(--line)",
  axisText: "var(--ink-muted)",
};

// Status → colors. Green/red are the bar composition; the tick uses a solid
// status color. Mirrors the semantic palette used elsewhere in the runs UI.
const STATUS_TICK_COLOR: Record<RunStatus, string> = {
  completed: CHART_COLORS.matched,
  failed: CHART_COLORS.failed,
  running: "var(--accent)",
  pending: "var(--ink-muted)",
};

const STATUS_LABEL: Record<RunStatus, string> = {
  completed: "Completed",
  failed: "Failed",
  running: "Running",
  pending: "Pending",
};

// Failed / running / pending bars are de-emphasised so completed runs read as
// the primary signal.
function barOpacity(status: RunStatus): number {
  return status === "completed" ? 1 : 0.45;
}

function runTimestamp(run: ScheduledJobRun): number {
  const t = Date.parse(run.startedAt ?? run.createdAt);
  return Number.isFinite(t) ? t : 0;
}

function formatTime(ts: number): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDayLabel(ts: number): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleDateString([], {
    month: "short",
    day: "numeric",
  });
}

function dayKey(ts: number): string {
  if (!ts) return "—";
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function formatDuration(run: ScheduledJobRun): string {
  if (!run.startedAt) return "—";
  const start = Date.parse(run.startedAt);
  const end = run.completedAt ? Date.parse(run.completedAt) : Date.now();
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return "—";
  }
  const totalSec = Math.floor((end - start) / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}m ${sec}s`;
  const hr = Math.floor(min / 60);
  return `${hr}h ${min % 60}m`;
}

interface HoverState {
  run: ScheduledJobRun;
  runId: string;
  // bd-hfv7u.17 — the hovered bar's bounding box in VIEWPORT coordinates
  // (getBoundingClientRect), so the portal tooltip (position: fixed) can be
  // placed relative to the real on-screen bar and clamped to the window.
  barRect: { left: number; top: number; right: number; bottom: number };
}

// bd-hfv7u.17 — gap between the hovered bar and the tooltip, and the minimum
// margin the tooltip keeps from every viewport edge.
const TOOLTIP_GAP = 10;
const VIEWPORT_MARGIN = 8;

export interface TooltipPlacement {
  left: number; // viewport x for the tooltip's left edge (position: fixed)
  top: number; // viewport y for the tooltip's top edge
  placement: "above" | "below" | "side";
}

// bd-hfv7u.17 — pure positioning helper (exported for unit testing). Given the
// hovered bar's viewport rect, the tooltip's natural size, and the viewport
// size, return a clamped {left, top} for a `position: fixed` tooltip that:
//   • floats at NATURAL width (never wraps/compresses — we reposition instead),
//   • sits ABOVE the bar by default so ≥50% of the bar stays visible,
//   • falls to BELOW, then to the SIDE, when there isn't room above,
//   • is clamped so it never leaves the viewport on any edge.
export function computeTooltipPosition(
  bar: { left: number; top: number; right: number; bottom: number },
  tip: { width: number; height: number },
  viewport: { width: number; height: number },
  gap: number = TOOLTIP_GAP,
  margin: number = VIEWPORT_MARGIN,
): TooltipPlacement {
  const barCenterX = (bar.left + bar.right) / 2;

  // Horizontal: centre on the bar, then clamp both edges into the viewport.
  const maxLeft = Math.max(margin, viewport.width - tip.width - margin);
  const rawLeft = barCenterX - tip.width / 2;
  const left = Math.min(Math.max(rawLeft, margin), maxLeft);

  // Vertical: prefer ABOVE the bar (bar stays visible below the tooltip).
  const roomAbove = bar.top - margin;
  const roomBelow = viewport.height - bar.bottom - margin;

  let top: number;
  let placement: TooltipPlacement["placement"];
  if (tip.height + gap <= roomAbove) {
    // Enough room above — sit above the bar's top edge.
    top = bar.top - gap - tip.height;
    placement = "above";
  } else if (tip.height + gap <= roomBelow) {
    // Not enough above but room below — sit below the bar.
    top = bar.bottom + gap;
    placement = "below";
  } else {
    // No room above or below (very tall tooltip / short viewport): place to the
    // SIDE of the bar rather than covering it — flip to whichever side has room —
    // and vertically clamp so the whole tooltip stays on-screen.
    placement = "side";
    const roomRight = viewport.width - bar.right - margin;
    const sideLeft =
      roomRight >= tip.width + gap
        ? bar.right + gap // room on the right
        : bar.left - gap - tip.width; // otherwise the left
    // Override the horizontal clamp for the side case, then re-clamp.
    const clampedSideLeft = Math.min(Math.max(sideLeft, margin), maxLeft);
    const maxTop = Math.max(margin, viewport.height - tip.height - margin);
    const centeredTop = (bar.top + bar.bottom) / 2 - tip.height / 2;
    return {
      left: clampedSideLeft,
      top: Math.min(Math.max(centeredTop, margin), maxTop),
      placement,
    };
  }

  // Final vertical clamp (guards a tooltip taller than the chosen gap slot).
  const maxTop = Math.max(margin, viewport.height - tip.height - margin);
  top = Math.min(Math.max(top, margin), maxTop);
  return { left, top, placement };
}

// bd-hfv7u.13 — fulfillment line color, drawn dashed to read as a secondary
// trend distinct from the solid match-rate line.
const FULFILLMENT_COLOR = CHART_COLORS.droppedOrFulfillment;

// bd-deepcontext-qx0pm — dropped/not-classified band color. The top stacked
// band of each 0–100 composition bar, carved out of what previously read as
// the pink unmatched remainder. A zero-drop run leaves this band at zero
// height (fully covered by pink), so it renders identically to the pre-drop UI.
const DROPPED_COLOR = CHART_COLORS.droppedOrFulfillment;

// bd-hfv7u.16 — SQUARE-ROOT Y scale. Match rates cluster sub-30%, so on a linear
// axis they hug the bottom and run-to-run variation is invisible. A √-scale
// stretches the low band (e.g. 10% lands ~31% up the axis) and compresses the
// high band. Maps a value `v` in percent (0..100) to its vertical position
// fraction from the BOTTOM of the plot. The scaling is VISUAL ONLY — tooltip
// values stay true percentages. e.g. yScaleFraction(25) → √0.25 = 0.5.
export function yScaleFraction(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return 0;
  const f = v / 100;
  if (f >= 1) return 1;
  return Math.sqrt(f);
}

// Gridline values (%) per the bd-hfv7u.16 mockup: widely spaced at the bottom,
// dense near the top once positioned via the √ mapping.
const Y_GRID_TICKS = [0, 5, 10, 20, 30, 50, 75, 100];

const JobRunsChart: React.FC<JobRunsChartProps> = ({
  runs,
  selectedRunId,
  onSelectRun,
  showHeader = true,
  urlsPerRun,
}) => {
  const [hover, setHover] = useState<HoverState | null>(null);
  // bd-hfv7u.17 — the portal tooltip renders at its natural size into
  // document.body; after render we measure it and compute a clamped, viewport-
  // fixed position (above the bar, flipped to the side/below near the edges).
  // `tipPos = null` on the first hover frame keeps the tooltip invisible until
  // it has been measured, so it never flashes at (0,0).
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [tipPos, setTipPos] = useState<TooltipPlacement | null>(null);

  useLayoutEffect(() => {
    if (!hover || !tipRef.current) {
      setTipPos(null);
      return;
    }
    const el = tipRef.current;
    const rect = el.getBoundingClientRect();
    setTipPos(
      computeTooltipPosition(
        hover.barRect,
        { width: rect.width, height: rect.height },
        { width: window.innerWidth, height: window.innerHeight },
      ),
    );
    // Re-measure on scroll/resize so the fixed tooltip tracks the bar while it
    // is shown.
    const reposition = () => {
      if (!tipRef.current) return;
      const r = tipRef.current.getBoundingClientRect();
      setTipPos(
        computeTooltipPosition(
          hover.barRect,
          { width: r.width, height: r.height },
          { width: window.innerWidth, height: window.innerHeight },
        ),
      );
    };
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [hover]);

  // Chronological order (oldest → newest), so the trend reads left-to-right.
  const ordered = useMemo(
    () => [...runs].sort((a, b) => runTimestamp(a) - runTimestamp(b)),
    [runs],
  );

  if (ordered.length === 0) return null;

  const count = ordered.length;
  // Bars are distributed across the fixed plot area (right of the Y axis), so the
  // chart always fills the full width of the modal body. Each run gets an equal
  // slot; the bar sits centred in its slot at half-slot width (clamped legible).
  const plotW = PLOT_WIDTH - Y_AXIS_W - RIGHT_PAD;
  const slot = plotW / count;
  const barW = Math.max(2, Math.min(44, slot * 0.5));
  const width = PLOT_WIDTH;
  const totalHeight = TOP_PAD + PLOT_HEIGHT + TICK_AREA + AXIS_HEIGHT;
  const baselineY = TOP_PAD + PLOT_HEIGHT; // y of the bars' bottom

  // Centre x of slot i (within the plot area, right of the Y axis gutter).
  const slotCenter = (i: number) => Y_AXIS_W + slot * i + slot / 2;

  // bd-hfv7u.13 — only draw the fulfillment line when a usable target exists
  // (urlsPerRun > 0); otherwise omit it (and its legend/tooltip rows) rather
  // than divide by zero. The same current target is the denominator for every
  // run in the window — no per-run historical target is stored.
  const hasFulfillment =
    typeof urlsPerRun === "number" && Number.isFinite(urlsPerRun) && urlsPerRun > 0;

  // Per-run geometry, computed once.
  // bd-hfv7u.16 — vertical positions go through the √ Y scale: the green bar
  // top, the match-rate line, and the fulfillment line all sit at
  // y = TOP_PAD + PLOT_HEIGHT * (1 - √(rate)). The rates are fractions [0,1], so
  // we pass them as percent (× 100) to `yScaleFraction`. Tooltip values are
  // unaffected — they read the raw rates directly.
  const points = ordered.map((run, i) => {
    const rate = runMatchRate(run);
    const x = slotCenter(i) - barW / 2;
    const greenH = yScaleFraction(rate * 100) * PLOT_HEIGHT;
    const greenTopY = baselineY - greenH;
    // bd-deepcontext-qx0pm — the pink (unmatched) band's TOP boundary. The bar
    // splits into three √-scaled stacked bands as fractions of processed
    // (matched + unmatched + dropped = 100%): green (bottom), pink (middle),
    // amber dropped (top). Pink spans from the green top up to the cumulative
    // (matched + unmatched) fraction = (1 − droppedRate). A zero-drop run has
    // pinkTop at 100% → pink fills to the top and amber is zero-height, so the
    // bar renders identically to the pre-drop UI.
    const droppedR = runDroppedRate(run);
    const pinkTopY = baselineY - yScaleFraction((1 - droppedR) * 100) * PLOT_HEIGHT;
    // Fulfillment rate (urlsProcessed / urlsPerRun), on the same √ Y axis.
    const fulfillRate = runFulfillmentRate(run, urlsPerRun);
    const fulfillY = TOP_PAD + PLOT_HEIGHT * (1 - yScaleFraction(fulfillRate * 100));
    return { run, i, x, rate, greenH, greenTopY, pinkTopY, fulfillRate, fulfillY };
  });

  // bd-hfv7u.16 — Y axis gridlines + labels at 0/5/10/20/30/50/75/100 (%),
  // positioned via the √ mapping (widely spaced at the bottom, dense near the top).
  const yTicks = Y_GRID_TICKS.map((v) => ({
    v,
    y: TOP_PAD + PLOT_HEIGHT * (1 - yScaleFraction(v)),
  }));

  // Trend polyline across match-rate boundaries (center of each slot top).
  const trend = points
    .map((p) => `${slotCenter(p.i)},${p.greenTopY}`)
    .join(" ");

  // bd-hfv7u.13 — fulfillment trend polyline (amber dashed), same axis as match
  // rate. Only built when a target exists.
  const fulfillTrend = hasFulfillment
    ? points.map((p) => `${slotCenter(p.i)},${p.fulfillY}`).join(" ")
    : "";

  // Day-group boundaries for axis dividers + labels.
  const dayGroups: { key: string; startIdx: number; endIdx: number }[] = [];
  points.forEach((p, idx) => {
    const key = dayKey(runTimestamp(p.run));
    const last = dayGroups[dayGroups.length - 1];
    if (last && last.key === key) {
      last.endIdx = idx;
    } else {
      dayGroups.push({ key, startIdx: idx, endIdx: idx });
    }
  });

  // bd-deepcontext-030xr — in the modal (showHeader === false) the chart is
  // dropped into a fixed-height card and must FILL it top-to-bottom so no white
  // gap shows below the plot. It fills by having the wrapper chain carry height
  // (h-full) and the SVG render at height 100% — preserveAspectRatio="none"
  // stretches the viewBox vertically to the card height with no clipping. The
  // standalone (showHeader) path keeps its natural fixed height.
  const fill = !showHeader;

  return (
    <div className={fill ? "h-full" : "mb-6"}>
      {showHeader && (
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-ink">
            Match rate by run
          </h4>
          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-muted">
            {/* bd-deepcontext-od1h — Tailwind arbitrary-value classes, not
                inline `style`: the class strings must stay literal so
                Tailwind's content scanner can see them (a value built from a
                JS constant at runtime never gets compiled). Keep each one in
                sync with CHART_COLORS and the matching SVG mark. */}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--safe-fg)]" />
              Matched
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--chart-risk-soft)]" />
              Unmatched
            </span>
            {/* bd-deepcontext-qx0pm — dropped/not-classified band chip. */}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--warn-fg)]" />
              Dropped (not classified)
            </span>
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--ink)]" />
              Match-rate trend
            </span>
            {hasFulfillment && (
              <span className="inline-flex items-center gap-1">
                <span className="inline-block w-4 border-t-2 border-dashed border-[var(--warn-fg)]" />
                Fulfillment rate
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[var(--risk-fg)]" />
              Failed
            </span>
          </div>
        </div>
      )}

      <div className={fill ? "relative w-full h-full" : "relative w-full"}>
        <svg
          viewBox={`0 0 ${width} ${totalHeight}`}
          width="100%"
          height={fill ? "100%" : totalHeight}
          preserveAspectRatio="none"
          role="img"
          aria-label="Match rate per job run"
          style={{ display: "block", overflow: "visible" }}
        >
          {/* bd-hfv7u.16 — √-scaled Y axis: 0/5/10/20/30/50/75/100 (%) gridlines
              + labels in the left gutter, dense near the top. */}
          {yTicks.map((t) => (
            <g key={`y-${t.v}`}>
              <line
                x1={Y_AXIS_W}
                y1={t.y}
                x2={width}
                y2={t.y}
                stroke={CHART_COLORS.gridline}
                strokeWidth={1}
              />
              <text
                x={Y_AXIS_W - 7}
                y={t.y + 3}
                textAnchor="end"
                fontSize="9"
                fill={CHART_COLORS.axisText}
              >
                {t.v}
              </text>
            </g>
          ))}

          {/* Day dividers + labels */}
          {dayGroups.map((g, gi) => {
            const gx = slotCenter(g.startIdx) - slot / 2;
            const gw = (g.endIdx - g.startIdx + 1) * slot;
            const labelTs = runTimestamp(points[g.startIdx].run);
            return (
              <g key={`day-${g.key}-${gi}`}>
                {gi > 0 && (
                  <line
                    x1={gx}
                    y1={TOP_PAD}
                    x2={gx}
                    y2={baselineY + TICK_AREA}
                    stroke={CHART_COLORS.gridline}
                    strokeWidth={1}
                    strokeDasharray="2 2"
                  />
                )}
                <text
                  x={gx + gw / 2}
                  y={baselineY + TICK_AREA + AXIS_HEIGHT - 4}
                  textAnchor="middle"
                  fontSize="10"
                  fill={CHART_COLORS.axisText}
                  fontWeight="600"
                >
                  {formatDayLabel(labelTs)}
                </text>
              </g>
            );
          })}

          {/* Baseline */}
          <line
            x1={Y_AXIS_W}
            y1={baselineY}
            x2={width}
            y2={baselineY}
            stroke={CHART_COLORS.gridline}
            strokeWidth={1}
          />

          {/* Bars (red remainder + green matched), status tick, time label */}
          {points.map((p) => {
            const { run, x, greenH, greenTopY, pinkTopY } = p;
            const status = run.status;
            const opacity = barOpacity(status);
            const selected = run.id === selectedRunId;
            const hovered = hover?.runId === run.id;
            return (
              <g
                key={run.id}
                style={{ cursor: "pointer" }}
                opacity={opacity}
                onClick={() => onSelectRun(run.id)}
                onMouseEnter={(e) =>
                  // bd-hfv7u.17 — capture the hovered bar's VIEWPORT rect so the
                  // portal tooltip (position: fixed) is placed against the real
                  // on-screen bar and clamped to the window edges.
                  setHover({
                    run,
                    runId: run.id,
                    barRect: (
                      e.currentTarget as SVGGElement
                    ).getBoundingClientRect(),
                  })
                }
                onMouseLeave={() => setHover(null)}
              >
                {/* bd-hfv7u.17 — hovered-column highlight: a soft band behind the
                    hovered bar so it's clear which run the tooltip describes. */}
                {hovered && (
                  <rect
                    x={x - 3}
                    y={TOP_PAD - 3}
                    width={barW + 6}
                    height={PLOT_HEIGHT + TICK_AREA + 6}
                    rx={3}
                    fill={CHART_COLORS.trend}
                    fillOpacity={0.06}
                    stroke={CHART_COLORS.trend}
                    strokeOpacity={0.35}
                    strokeWidth={1}
                    pointerEvents="none"
                  />
                )}
                {/* Selection highlight behind the bar */}
                {selected && (
                  <rect
                    x={x - 2}
                    y={TOP_PAD - 2}
                    width={barW + 4}
                    height={PLOT_HEIGHT + 4}
                    rx={3}
                    fill="none"
                    stroke="var(--accent)"
                    strokeWidth={2}
                  />
                )}
                {/* bd-deepcontext-qx0pm — three stacked bands. Drawn
                    bottom-covering-top so the boundaries read cleanly:
                    (1) amber dropped as the full-bar background (top band),
                    (2) pink unmatched overlaid from the baseline up to
                        pinkTopY (covers amber below its boundary),
                    (3) green matched overlaid from the baseline up to
                        greenTopY (covers pink below its boundary).
                    A zero-drop run has pinkTopY at the plot top, so pink fully
                    covers amber and the bar looks identical to the pre-drop UI. */}
                <rect
                  x={x}
                  y={TOP_PAD}
                  width={barW}
                  height={PLOT_HEIGHT}
                  rx={2}
                  fill={DROPPED_COLOR}
                />
                {/* Pink unmatched band (from the baseline up to pinkTopY) */}
                <rect
                  x={x}
                  y={pinkTopY}
                  width={barW}
                  height={baselineY - pinkTopY}
                  rx={2}
                  fill={CHART_COLORS.unmatched}
                />
                {/* Green matched portion, anchored to the baseline */}
                <rect
                  x={x}
                  y={greenTopY}
                  width={barW}
                  height={greenH}
                  rx={2}
                  fill={CHART_COLORS.matched}
                />
                {/* Status tick under the bar */}
                <rect
                  x={x}
                  y={baselineY + 4}
                  width={barW}
                  height={4}
                  rx={1}
                  fill={STATUS_TICK_COLOR[status]}
                />
                {/* Run time on the axis */}
                <text
                  x={x + barW / 2}
                  y={baselineY + TICK_AREA + 11}
                  textAnchor="middle"
                  fontSize="9"
                  fill={CHART_COLORS.axisText}
                >
                  {formatTime(runTimestamp(run))}
                </text>
              </g>
            );
          })}

          {/* bd-hfv7u.13 — fulfillment trend (amber dashed), same 0–100% axis as
              the match-rate line. Drawn before the blue line so match rate (the
              primary signal) stays on top. */}
          {hasFulfillment && points.length > 1 && (
            <polyline
              points={fulfillTrend}
              fill="none"
              stroke={FULFILLMENT_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 3"
              strokeLinejoin="round"
              strokeLinecap="round"
              pointerEvents="none"
            />
          )}

          {/* Boundary trend polyline (drawn last so it sits on top) */}
          {points.length > 1 && (
            <polyline
              points={trend}
              fill="none"
              stroke={CHART_COLORS.trend}
              strokeWidth={1.5}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.85}
              pointerEvents="none"
            />
          )}
        </svg>

        {/* bd-hfv7u.17 — Hover tooltip rendered through a PORTAL into
            document.body with position: fixed + a high z-index, so no ancestor
            overflow/width can clip or compress it. It keeps its natural width
            (whitespace-nowrap) and is repositioned — above the bar, flipped to
            the side/below and clamped to the viewport near the edges — via
            computeTooltipPosition once measured. `visibility:hidden` on the
            first frame (before measurement) avoids a flash at (0,0). */}
        {hover &&
          createPortal(
            <div
              ref={tipRef}
              className="pointer-events-none bg-ink text-canvas text-xs rounded-md shadow-lg px-3 py-2 whitespace-nowrap"
              style={{
                position: "fixed",
                zIndex: 9999,
                left: tipPos ? `${tipPos.left}px` : 0,
                top: tipPos ? `${tipPos.top}px` : 0,
                visibility: tipPos ? "visible" : "hidden",
              }}
            >
            <div className="font-semibold mb-1">
              {formatTime(runTimestamp(hover.run))} ·{" "}
              {STATUS_LABEL[hover.run.status]}
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
              {/* bd-hfv7u.14 — row order: Requested, Processed, Matched,
                  Unmatched, Match rate, Duration, Avg confidence (matched). The
                  Requested row only appears when a per-run target is available;
                  Avg confidence appears for completed runs only. */}
              {hasFulfillment && (
                <>
                  <span className="text-ink-muted">Requested</span>
                  <span className="text-right font-medium">{urlsPerRun}</span>
                </>
              )}
              <span className="text-ink-muted">Processed</span>
              <span className="text-right font-medium">
                {hover.run.urlsProcessed}
              </span>
              <span className="text-ink-muted">Matched</span>
              <span className="text-right font-medium text-safe">
                {hover.run.resultsSummary?.matchedUrls ?? "—"}
              </span>
              <span className="text-ink-muted">Unmatched</span>
              <span className="text-right font-medium text-risk">
                {hover.run.resultsSummary?.unmatchedUrls ?? "—"}
              </span>
              {/* bd-deepcontext-qx0pm — dropped/not-classified row, between
                  Unmatched and Match rate (bd-hfv7u.14 order). Shown only for
                  runs that actually dropped URLs; the count + % of processed
                  are read straight from the summary (match-rate math untouched). */}
              {(hover.run.resultsSummary?.droppedUrls ?? 0) > 0 && (
                <>
                  <span className="text-ink-muted">Dropped (not classified)</span>
                  <span className="text-right font-medium text-warn">
                    {hover.run.resultsSummary!.droppedUrls} ·{" "}
                    {(runDroppedRate(hover.run) * 100).toFixed(1)}%
                  </span>
                </>
              )}
              <span className="text-ink-muted">Match rate</span>
              <span className="text-right font-medium">
                {(runMatchRate(hover.run) * 100).toFixed(1)}%
              </span>
              <span className="text-ink-muted">Duration</span>
              <span className="text-right font-medium">
                {formatDuration(hover.run)}
              </span>
              {/* bd-hfv7u.14 — Avg confidence (matched), moved here from the
                  per-run detail panel (removed in bd-hfv7u.15). Completed runs
                  only: it's matched-only and reads 0 mid-run, so the row is
                  omitted otherwise. */}
              {hover.run.status === "completed" &&
                Number.isFinite(
                  hover.run.resultsSummary?.averageConfidence,
                ) && (
                  <>
                    <span className="text-ink-muted">Avg confidence (matched)</span>
                    <span className="text-right font-medium">
                      {(
                        (hover.run.resultsSummary!.averageConfidence ?? 0) * 100
                      ).toFixed(1)}
                      %
                    </span>
                  </>
                )}
            </div>
            {hasFulfillment && (
              <div className="mt-1 max-w-[14rem] whitespace-normal text-xs leading-tight text-ink-muted">
                Fulfillment uses the job's current target ({urlsPerRun}) for all
                runs — no per-run historical target is stored.
              </div>
            )}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default JobRunsChart;
